import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { getIndicatorClassification, IndicatorEsfEnum } from "@/utils/indicatorsEsf";
import { ResumeIndicatorC7ByHealthTeamData } from "@/services/esus/indicator/esf/c7/getResumeIndicatorC7ByHealthTeam";

import { IndicatorEsfC7HealthTeamsFilters } from "./IndicatorEsfC7HealthTeamsFilters";
import { IndicatorEsfC7HealthTeamsClassificationTable } from "./IndicatorEsfC7HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC7HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorC7ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorC7ByHealthTeam: () => Promise<ResumeIndicatorC7ByHealthTeamData[]>;
};

export function IndicatorEsfC7HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorC7ByHealthTeam,
}: Readonly<IndicatorEsfC7HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);
	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorC7ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorC7AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Pontuações por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC7AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorC7ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe do atendimento": healthTeamClassification.healthTeam?.name ?? "",
					"Exame câncer do colo de útero (concluídos)":
						healthTeamClassification.totalCervicalCancerCitizensFinished,
					"Exame câncer do colo de útero (identificados)":
						healthTeamClassification.totalCervicalCancerCitizens,
					"Exame câncer do colo de útero (%)": `${calculatePercentage(
						healthTeamClassification.totalCervicalCancerCitizensFinished,
						healthTeamClassification.totalCervicalCancerCitizens
					)}%`,
					"Vacinações HPV (concluídos)":
						healthTeamClassification.totalVaccinationCitizensFinished,
					"Vacinações HPV (identificados)":
						healthTeamClassification.totalVaccinationCitizens,
					"Vacinações HPV (%)": `${calculatePercentage(
						healthTeamClassification.totalVaccinationCitizensFinished,
						healthTeamClassification.totalVaccinationCitizens
					)}%`,
					"Consultas (concluídos)":
						healthTeamClassification.totalAttendanceCitizensFinished,
					"Consultas (identificados)": healthTeamClassification.totalAttendanceCitizens,
					"Consultas (%)": `${calculatePercentage(
						healthTeamClassification.totalAttendanceCitizensFinished,
						healthTeamClassification.totalAttendanceCitizens
					)}%`,
					"Exame câncer de mama (concluídos)":
						healthTeamClassification.totalBreastCancerCitizensFinished,
					"Exame câncer de mama (identificados)":
						healthTeamClassification.totalBreastCancerCitizens,
					"Exame câncer de mama (%)": `${calculatePercentage(
						healthTeamClassification.totalBreastCancerCitizensFinished,
						healthTeamClassification.totalBreastCancerCitizens
					)}%`,
					"% Classificação": `${healthTeamClassification.weightedPercentage}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsfEnum.C7,
						healthTeamClassification.weightedPercentage
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC7HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC7HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
